import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';

const COLORS = ['#4f46e5', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function FinancialReports() {
  const { user } = useAuth();
  const [incomes, setIncomes] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [inc, exp] = await Promise.all([api.get('/incomes'), api.get('/expenses')]);
      const incData = (inc.data?.data || inc.data || []);
      const expData = (exp.data?.data || exp.data || []);
      setIncomes(incData);
      setExpenses(expData);
    } catch (e) { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: user?.currency || 'USD' }).format(v || 0);
  const totalIncome = incomes.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const totalExpenses = expenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const year = new Date().getFullYear();

  const monthlyData = MONTHS.map((m, idx) => {
    const mn = idx + 1;
    const mi = incomes.filter(i => new Date(i.date).getFullYear() === year && new Date(i.date).getMonth() + 1 === mn).reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
    const me = expenses.filter(e => new Date(e.date).getFullYear() === year && new Date(e.date).getMonth() + 1 === mn).reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
    return { month: m, income: mi, expenses: me, savings: mi - me };
  });

  const categoryData = expenses.reduce((acc, e) => {
    const cat = e.category || 'Other';
    const ex = acc.find(a => a.name === cat);
    if (ex) ex.value += parseFloat(e.amount) || 0;
    else acc.push({ name: cat, value: parseFloat(e.amount) || 0 });
    return acc;
  }, []);

  const handleExport = async (format) => {
    setExporting(true);
    try {
      if (format === 'csv') {
        const res = await api.get('/reports/export?format=csv', { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
        const a = document.createElement('a'); a.href = url; a.download = `finovatrack-report-${new Date().toISOString().split('T')[0]}.csv`; a.click();
        window.URL.revokeObjectURL(url);
        toast.success('CSV downloaded!');
      } else if (format === 'excel') {
        const res = await api.get('/reports/export?format=csv', { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.ms-excel' }));
        const a = document.createElement('a'); a.href = url; a.download = `finovatrack-report-${new Date().toISOString().split('T')[0]}.xls`; a.click();
        window.URL.revokeObjectURL(url);
        toast.success('Excel downloaded!');
      } else if (format === 'pdf') {
        const res = await api.get('/reports/export');
        const report = res.data.data;
        const w = window.open('', '_blank');
        w.document.write(`<html><head><title>FinovaTrack Report</title><style>body{font-family:Arial;margin:30px}h1{color:#4f46e5}table{width:100%;border-collapse:collapse;margin:15px 0}th,td{border:1px solid #ddd;padding:10px;text-align:left}th{background:#4f46e5;color:white}tr:nth-child(even){background:#f9f9f9}.total{font-size:18px;font-weight:bold;margin:10px 0}.summary{background:#f0f0ff;padding:15px;border-radius:8px;margin:15px 0}</style></head><body>`);
        w.document.write(`<h1>📊 FinovaTrack Financial Report</h1><p>Generated: ${new Date().toLocaleString()}</p>`);
        w.document.write(`<div class="summary"><p class="total">💰 Total Income: ${formatCurrency(report.totalIncome)}</p><p class="total">💸 Total Expenses: ${formatCurrency(report.totalExpenses)}</p><p class="total">📈 Net Balance: ${formatCurrency(report.totalIncome - report.totalExpenses)}</p></div>`);
        w.document.write(`<h2>Income (${report.incomes.length} records)</h2><table><tr><th>Date</th><th>Category</th><th>Amount</th><th>Description</th></tr>`);
        report.incomes.forEach(i => w.document.write(`<tr><td>${i.date}</td><td>${i.category}</td><td>${formatCurrency(i.amount)}</td><td>${i.description||''}</td></tr>`));
        w.document.write('</table>');
        w.document.write(`<h2>Expenses (${report.expenses.length} records)</h2><table><tr><th>Date</th><th>Category</th><th>Amount</th><th>Description</th></tr>`);
        report.expenses.forEach(e => w.document.write(`<tr><td>${e.date}</td><td>${e.category}</td><td>${formatCurrency(e.amount)}</td><td>${e.description||''}</td></tr>`));
        w.document.write('</table></body></html>');
        w.document.close();
        setTimeout(() => { w.print(); toast.success('PDF report ready for printing!'); }, 800);
      }
    } catch (e) { toast.error('Export failed. Please try again.'); console.error(e); }
    finally { setExporting(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div></div>;

  const hasData = incomes.length > 0 || expenses.length > 0;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div><h1 className="text-2xl font-bold text-gray-900">Financial Reports</h1><p className="text-gray-500">{incomes.length + expenses.length} total transactions</p></div>
        <div className="flex gap-2">
          <button onClick={() => handleExport('csv')} disabled={exporting || !hasData} className="bg-green-600 text-white px-4 py-2.5 rounded-xl hover:bg-green-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed">📥 CSV</button>
          <button onClick={() => handleExport('excel')} disabled={exporting || !hasData} className="bg-blue-600 text-white px-4 py-2.5 rounded-xl hover:bg-blue-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed">📊 Excel</button>
          <button onClick={() => handleExport('pdf')} disabled={exporting || !hasData} className="bg-red-600 text-white px-4 py-2.5 rounded-xl hover:bg-red-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed">📄 PDF</button>
        </div>
      </div>

      {!hasData ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-16 text-center">
          <div className="text-6xl mb-4">📊</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Data Yet</h2>
          <p className="text-gray-500 mb-6">Add income or expenses to generate reports. Reports can be exported as CSV, Excel, or PDF.</p>
          <div className="flex gap-3 justify-center">
            <a href="/income-tracker" className="bg-green-600 text-white px-6 py-3 rounded-xl font-medium hover:bg-green-700">Add Income →</a>
            <a href="/expense-tracker" className="bg-indigo-600 text-white px-6 py-3 rounded-xl font-medium hover:bg-indigo-700">Add Expense →</a>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[{l:'Total Income',v:formatCurrency(totalIncome),c:'green'},{l:'Total Expenses',v:formatCurrency(totalExpenses),c:'red'},{l:'Net Balance',v:formatCurrency(totalIncome-totalExpenses),c:'indigo'},{l:'Transactions',v:incomes.length+expenses.length,c:'purple'}].map((s,i)=>(
              <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5"><p className="text-xs text-gray-500 uppercase">{s.l}</p><p className={`text-2xl font-bold text-${s.c}-600 mt-1`}>{s.v}</p></div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"><h3 className="font-semibold mb-4">📊 Monthly Income vs Expenses ({year})</h3><ResponsiveContainer width="100%" height={300}><BarChart data={monthlyData}><CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/><XAxis dataKey="month" tick={{fontSize:11}}/><YAxis tick={{fontSize:11}} tickFormatter={v=>'$'+(v>=1000?(v/1000).toFixed(0)+'k':v)}/><Tooltip formatter={v=>formatCurrency(v)}/><Bar dataKey="income" fill="#10b981" radius={[4,4,0,0]} name="Income"/><Bar dataKey="expenses" fill="#ef4444" radius={[4,4,0,0]} name="Expenses"/></BarChart></ResponsiveContainer></div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"><h3 className="font-semibold mb-4">🍩 Expense Categories</h3>{categoryData.length>0?<ResponsiveContainer width="100%" height={300}><PieChart><Pie data={categoryData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={3} dataKey="value">{categoryData.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}</Pie><Tooltip formatter={v=>formatCurrency(v)}/></PieChart></ResponsiveContainer>:<p className="text-gray-400 text-center py-16">No expenses</p>}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"><h3 className="font-semibold mb-4">📈 Savings Trend ({year})</h3><ResponsiveContainer width="100%" height={300}><LineChart data={monthlyData}><CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/><XAxis dataKey="month" tick={{fontSize:11}}/><YAxis tick={{fontSize:11}} tickFormatter={v=>'$'+(v>=1000?(v/1000).toFixed(0)+'k':v)}/><Tooltip formatter={v=>formatCurrency(v)}/><Line type="monotone" dataKey="savings" stroke="#6366f1" strokeWidth={3} dot={{r:4}} name="Savings"/></LineChart></ResponsiveContainer></div>
        </>
      )}
    </div>
  );
}